import { trpc } from "@utils/trpc";
import { APP_ONLY_PLAYLIST_IDS } from "@utils/collections/playlists";
import {
  useQuery,
  useSuspenseQuery,
  type UseQueryOptions,
  type UseSuspenseQueryOptions,
} from "@tanstack/react-query";
import { Song } from "@libs/wire";
import { PlaylistWithSongs } from "@libs/wire";
import { useMe } from "@hooks/trpc/useMe";
import { useDownloads } from "@utils/downloadManager/hooks/useDownloads";
import { useOfflineLibrary } from "@utils/downloadManager/hooks";
import { captureException } from "@utils/captureException";
import { playlistQuery, playlistQueryKey } from "./queryFunctions";
import { useData } from "../../../runtime/provider";
import type { DataSession } from "../../../data/core/session";
import {
  usePlayerStateReader,
  usePlayerActions,
} from "@utils/player/core/usePlayer";
import { createLocalSongAssets } from "@utils/player/data/localAudioPaths";
import { useEffect, useMemo } from "react";
import { toast as sonnerToast } from "sonner-native";
import {
  libraryQueryKey,
  type LibraryQueryData,
} from "../useLibrary/queryFunctions";

export { playlistQueryKey, playlistQuery } from "./queryFunctions";

// Post-Phase-2.3: queryFn returns a single PlaylistWithSongs (the old
// `select: data => data[0]` is dropped). The trpc-derived `ReactQueryOptions`
// type still keys off the array wire shape, so we define our own option
// type that matches the new single-object queryFn signature.
type QueryOptions = Omit<
  UseQueryOptions<
    PlaylistWithSongs,
    Error,
    PlaylistWithSongs,
    ReturnType<typeof playlistQueryKey>
  >,
  "queryKey" | "queryFn"
>;

type SuspenseQueryOptions = Omit<
  UseSuspenseQueryOptions<
    PlaylistWithSongs,
    Error,
    PlaylistWithSongs,
    ReturnType<typeof playlistQueryKey>
  >,
  "queryKey" | "queryFn"
>;

export const useRemoveSong = (options?: { disableToast?: boolean }) => {
  const getPlayerState = usePlayerStateReader();
  const playerActions = usePlayerActions();
  const downloads = useDownloads();
  const data = useData();
  const { queryClient, utils } = data;
  const removeSong = trpc.user.playlists.removeSong.useMutation({
    networkMode: "online",
    async onMutate({ id, songId }) {
      const player = getPlayerState();
      const activeQueue = player.activeQueue;

      if (
        activeQueue?.type === "playlist" &&
        activeQueue.collection.type === "playlist" &&
        activeQueue.collection.id === id
      ) {
        const activeItem = activeQueue.items[activeQueue.currentIndex];
        const activeSongId = activeItem?.metadata.id ?? null;

        if (activeSongId && activeSongId !== songId) {
          const queueItemId =
            activeQueue.items.find((item: any) => item.metadata.id === songId)
              ?.qid ?? null;

          if (queueItemId) {
            playerActions.removeFromQueue([queueItemId]);
          }
        }
      }

      utils.catalog.songs.getByPlaylistIds.setData([id], (prev) => {
        if (!prev) return prev;
        return prev.filter((song) => song.id !== songId);
      });

      data.cache.setQueryDataDurable<PlaylistWithSongs>(
        queryClient,
        playlistQueryKey(id),
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            songs: prev.songs.filter((song) => song.id !== songId),
          };
        },
      );

      data.cache.setQueryDataDurable<LibraryQueryData>(
        queryClient,
        libraryQueryKey(),
        (prev) => {
          if (!prev) return prev;
          return prev.map((item) => {
            if (item.type !== "playlist" || item.id !== id) return item;
            return {
              ...item,
              songIds: item.songIds.filter((itemId) => itemId !== songId),
              privateSongIds: item.privateSongIds.filter(
                (itemId) => itemId !== songId,
              ),
            };
          });
        },
      );

      await downloads.onRemoveSongFromPlaylist(id, songId).catch((err) => {
        console.error(
          "::useRemoveSong - downloadManger.onRemoveSongFromPlaylist error",
          err,
        );
      });
    },
    onSettled: (_, __, { id }) => {
      Promise.all([
        utils.catalog.songs.getByPlaylistIds.invalidate([id]),
        queryClient.invalidateQueries({
          queryKey: playlistQueryKey(id),
        }),
        queryClient.invalidateQueries({
          queryKey: libraryQueryKey(),
        }),
      ]).catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "playlist",
          operation: "invalidate-after-remove-song",
          tags: { playlistId: id },
        });
      });

      if (!options?.disableToast) {
        sonnerToast.success("Song removed from playlist");
      }
    },
  });

  return { removeSong };
};

/**
 * Get, update and delete a playlist that the user owns
 */
export const usePlaylist = (
  id: string,
  options?: QueryOptions & { disableToast?: boolean },
) => {
  const data = useData();
  const { queryClient, utils } = data;
  const downloads = useDownloads();
  const { removeSong } = useRemoveSong({
    disableToast: options?.disableToast,
  });
  // App-only playlist IDs (continuous_listening, explore_feed) live entirely
  // client-side and have no server representation — disable the query for
  // them so react-query doesn't fail on the queryFn's `undefined` return.
  // Consumers fall back to in-memory player state.
  const isAppOnly = !!id && APP_ONLY_PLAYLIST_IDS.includes(id);
  const enabled =
    options?.enabled !== undefined ? options?.enabled : !isAppOnly;
  const rawQuery = useQuery({
    ...playlistQuery(data, id),
    ...options,
    enabled,
  });

  // Reactive offline substitution mirrors useLibrary: when the playlist is
  // also in the user's offline_collections, swap `images` for the local
  // copy so artwork renders even on bad internet. Songs are NOT merged —
  // server is authoritative on the song list (downloads ⊂ library, ADR
  // 0001). updateOfflineCollection is fired as a side effect to keep the
  // offline snapshot's metadata in sync with the latest server response.
  const { offlineLibrary } = useOfflineLibrary();
  const offlinePlaylist = useMemo(() => {
    if (!offlineLibrary || !id) return undefined;
    return offlineLibrary.find((it) => it.type === "playlist" && it.id === id);
  }, [offlineLibrary, id]);

  useEffect(() => {
    if (rawQuery.data && offlinePlaylist) {
      downloads.updateOfflineCollection(id).catch(console.error);
    }
  }, [rawQuery.data, offlinePlaylist, id, downloads]);

  const composedData = useMemo(() => {
    if (!rawQuery.data) return rawQuery.data;
    if (!offlinePlaylist || offlinePlaylist.type !== "playlist") {
      return rawQuery.data;
    }
    return { ...rawQuery.data, images: offlinePlaylist.images };
  }, [rawQuery.data, offlinePlaylist]);

  const query = useMemo(
    () => ({ ...rawQuery, data: composedData }),
    [rawQuery, composedData],
  ) as typeof rawQuery;

  const updatePlaylist = trpc.user.playlists.update.useMutation({
    onSettled: () => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: playlistQueryKey(id),
        }),
        queryClient.invalidateQueries({
          queryKey: libraryQueryKey(),
        }),
      ]).catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "playlist",
          operation: "invalidate-after-playlist-update",
          tags: { playlistId: id },
        });
      });
    },
  });
  const deletePlaylist = trpc.user.playlists.delete.useMutation({
    // Only a confirmed deletion removes the saved copy, so the playlist
    // cannot re-appear after a relaunch or a bad-internet refetch.
    onSuccess: (_deleted, playlistId) =>
      data.cache.deleteDurableRow(playlistQueryKey(playlistId)),
    onSettled: () => {
      if (!id) return;
      queryClient.setQueryData<LibraryQueryData>(libraryQueryKey(), (prev) => {
        if (!prev) return prev;
        return prev.filter((playlist) => playlist.id !== id);
      });

      utils.catalog.playlists.listRecommendedPlaylists.setData(
        undefined,
        (prev) => {
          if (!prev) return prev;
          return prev.filter((playlist) => playlist.id !== id);
        },
      );
    },
  });

  return {
    query,
    updatePlaylist,
    deletePlaylist,
    removeSong,
  };
};

export const useCreatePlaylist = () => {
  const data = useData();
  const { queryClient, utils } = data;
  const {
    query: { data: user },
  } = useMe();

  const createPlaylistMutation = trpc.user.playlists.create.useMutation({
    onSettled: () => {
      queryClient
        .refetchQueries({ queryKey: libraryQueryKey() })
        .catch((error) => {
          captureException(error, {
            channel: "breadcrumb",
            area: "playlist",
            operation: "refetch-library-after-create-playlist",
          });
        });
    },
  });
  const createPlaylist = ({
    id,
    songToAdd,
    title,
    visibility,
    image,
  }: {
    id: string;
    songToAdd?: Song;
    title: string;
    visibility: PlaylistWithSongs["visibility"];
    image?: string;
  }) => {
    // Optimistic UI update
    const newPlaylist = {
      type: "playlist" as const,
      id,
      blurhash: null,
      hasCustomImage: !!image,
      images: image
        ? [image]
        : songToAdd?.albums.at(0)?.image
          ? [songToAdd.albums.at(0)?.image ?? ""]
          : [],
      title,
      songs: songToAdd ? [songToAdd] : [],
      user: {
        id: user?.id || "",
        name: user?.name || "",
      },
      visibility,
      hasPrivateSong: songToAdd?.private || false,
      privateSongIds: songToAdd?.private ? [songToAdd.id] : [],
      genreTags: [],
      customTags: [],
    };

    queryClient.setQueryData<LibraryQueryData>(libraryQueryKey(), (prev) => {
      // Add the new playlist after the discover daily playlist
      const previousLibrary = prev ?? [];
      const discoverDailyIndex = previousLibrary.findIndex(
        (item) => item.type === "playlist" && item.id === "discover_daily",
      );

      const indexToPushAt = discoverDailyIndex + 1;

      const newLibrary = [
        ...previousLibrary.slice(0, indexToPushAt),
        {
          ...newPlaylist,
          created_at: new Date().toISOString(),
          songIds: newPlaylist.songs.map((song) => song.id),
        },
        ...previousLibrary.slice(indexToPushAt),
      ];

      return newLibrary;
    });

    // Optimistic seed of the playlist's own cache. Write through to
    // durable cache so cold-start before the server confirms still
    // sees the new playlist.
    data.cache.setQueryDataDurable<PlaylistWithSongs>(
      queryClient,
      playlistQueryKey(id),
      () => newPlaylist,
    );

    // Cache the songs
    utils.catalog.songs.getByPlaylistIds.setData([id], () => {
      return songToAdd ? [songToAdd] : [];
    });

    createPlaylistMutation.mutate({
      id,
      songIds: songToAdd ? [songToAdd.id] : undefined,
      title,
      visibility,
      image,
    });
  };

  return { createPlaylist };
};

export const useAddSong = () => {
  const getPlayerState = usePlayerStateReader();
  const playerActions = usePlayerActions();
  const downloads = useDownloads();
  const data = useData();
  const { queryClient, utils } = data;
  const addSongToMutation = trpc.user.playlists.addSong.useMutation({
    networkMode: "online",
    onError() {
      sonnerToast.error("Failed to add song to playlist. Please try again.");
    },
    onSettled: (_, __, { playlistIds }) => {
      const invalidationsByPlaylistId = playlistIds.map((id) =>
        queryClient.invalidateQueries({
          queryKey: playlistQueryKey(id),
        }),
      );

      Promise.all([
        ...invalidationsByPlaylistId,
        utils.catalog.songs.getByPlaylistIds.invalidate(playlistIds),
        queryClient.invalidateQueries({
          queryKey: libraryQueryKey(),
        }),
      ])
        .then(() => {
          const player = getPlayerState();
          const activeQueue = player.activeQueue;

          if (
            activeQueue?.type === "playlist" &&
            activeQueue.collection.type === "playlist" &&
            playlistIds.includes(activeQueue.collection.id)
          ) {
            const freshSongs = utils.catalog.songs.getByPlaylistIds.getData([
              activeQueue.collection.id,
            ]);
            if (freshSongs) {
              // Same reason as `playCollection` tier 1: react-query stores
              // remote URLs; the queue must carry local file paths for
              // downloaded songs.
              playerActions.syncPlaylistQueue(
                createLocalSongAssets(data.db).substituteLocalSongAssets(
                  freshSongs,
                ),
              );
            }
          }
        })
        .catch((error) => {
          captureException(error, {
            channel: "breadcrumb",
            area: "playlist",
            operation: "sync-playlist-queue-after-add-song",
          });
        });
    },
  });

  const updateOfflinePlaylist = (song: Song, playlistId: string) => {
    downloads.onAddSongToPlaylist(playlistId, song).catch((err) => {
      console.error(
        "::usePlaylists - downloadManger.onAddSongToPlaylist error",
        err,
      );
    });
  };

  const addSong = (playlistIds: string[], song: Song) => {
    // Optimistic update: library cache
    data.cache.setQueryDataDurable<LibraryQueryData>(
      queryClient,
      libraryQueryKey(),
      (prev) => {
        if (!prev) return prev;

        return prev.map((item) => {
          if (
            item.type !== "playlist" ||
            !playlistIds.includes(item.id) ||
            item.songIds.includes(song.id)
          ) {
            return item;
          }
          const privateSongIds = item.privateSongIds ?? [];
          return {
            ...item,
            songIds: item.id.startsWith("liked_")
              ? [song.id, ...item.songIds]
              : [...item.songIds, song.id],
            privateSongIds: song.private
              ? [song.id, ...privateSongIds]
              : privateSongIds,
          };
        });
      },
    );

    // Optimistic update: getByPlaylistIds cache
    playlistIds.forEach((playlistId) => {
      data.cache.setQueryDataDurable<PlaylistWithSongs>(
        queryClient,
        playlistQueryKey(playlistId),
        (prev) => {
          if (!prev || prev.songs.some((item) => item.id === song.id)) {
            return prev;
          }
          return {
            ...prev,
            songs: playlistId.startsWith("liked_")
              ? [song, ...prev.songs]
              : [...prev.songs, song],
          };
        },
      );

      utils.catalog.songs.getByPlaylistIds.setData([playlistId], (prev) => {
        if (!prev) return prev;
        if (prev.some((s) => s.id === song.id)) return prev;
        return playlistId.startsWith("liked_")
          ? [song, ...prev]
          : [...prev, song];
      });
    });

    // Update downloaded playlist membership. The download manager reuses an
    // existing shared track file and only downloads when the track is absent.
    playlistIds.forEach((playlistId) => {
      updateOfflinePlaylist(song, playlistId);
    });

    addSongToMutation.mutate({
      playlistIds,
      songId: song.id,
    });
  };

  return { addSong };
};

export const usePlaylistSuspense = (
  id: string,
  options?: SuspenseQueryOptions,
) => {
  const data = useData();
  const config = playlistQuery(data, id);
  const query = useSuspenseQuery({
    ...config,
    ...options,
  });

  return { query };
};

export const prefetchPlaylist = (data: DataSession, id: string) => {
  const { queryClient } = data;
  const config = playlistQuery(data, id);
  return queryClient
    .prefetchQuery({
      queryKey: config.queryKey,
      queryFn: config.queryFn,
    })
    .catch((error) => {
      captureException(error, {
        channel: "breadcrumb",
        area: "playlist",
        operation: "prefetch-playlist",
        tags: { playlistId: id },
      });
    });
};
